import { useState, useMemo } from 'react';
import { actScienceWorksheets } from '../worksheets/worksheets-act-science';

type FilterOption = 'All' | 'Interpretation of Data' | 'Scientific Investigation' | 'Evaluation of Models' | 'Biology Content' | 'Chemistry Content' | 'Physics Content' | 'Earth/Space Content' | 'Data Representation' | 'Research Summaries' | 'Conflicting Viewpoints' | 'Full Practice';

const topicColors: Record<string, string> = {
  'Interpretation of Data': 'bg-blue-100 text-blue-700 border-blue-200',
  'Scientific Investigation': 'bg-green-100 text-green-700 border-green-200',
  'Evaluation of Models': 'bg-purple-100 text-purple-700 border-purple-200',
  'Biology Content': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Chemistry Content': 'bg-orange-100 text-orange-700 border-orange-200',
  'Physics Content': 'bg-red-100 text-red-700 border-red-200',
  'Earth/Space Content': 'bg-cyan-100 text-cyan-700 border-cyan-200',
  'Data Representation': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Research Summaries': 'bg-pink-100 text-pink-700 border-pink-200',
  'Conflicting Viewpoints': 'bg-amber-100 text-amber-700 border-amber-200',
  'Full Practice': 'bg-violet-100 text-violet-700 border-violet-200',
};

export default function ACTScienceWorksheetsPage() {
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredWorksheets = useMemo(() => {
    let filtered = actScienceWorksheets;

    if (selectedFilter !== 'All') {
      filtered = filtered.filter(w => w.topic === selectedFilter);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(w =>
        w.title.toLowerCase().includes(query) ||
        w.description.toLowerCase().includes(query) ||
        w.topic.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [selectedFilter, searchQuery]);

  const filterOptions: FilterOption[] = [
    'All',
    'Interpretation of Data',
    'Scientific Investigation',
    'Evaluation of Models',
    'Biology Content',
    'Chemistry Content',
    'Physics Content',
    'Earth/Space Content',
    'Data Representation',
    'Research Summaries',
    'Conflicting Viewpoints',
    'Full Practice',
  ];

  const getTopicCount = (topic: FilterOption) => {
    if (topic === 'All') return actScienceWorksheets.length;
    return actScienceWorksheets.filter(w => w.topic === topic).length;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <i className="ri-flask-line text-4xl"></i>
            <h1 className="text-4xl font-bold">ACT Science Worksheets</h1>
          </div>
          <p className="text-xl text-white/90 max-w-3xl mb-6">
            Browse and download 30+ topic-specific practice drills covering all ACT Science skills, passage types, and content areas
          </p>
          <div className="flex items-center gap-4 text-white/90">
            <div className="flex items-center gap-2">
              <i className="ri-file-list-3-line text-2xl"></i>
              <span className="font-semibold">{actScienceWorksheets.length} Worksheets</span>
            </div>
            <div className="w-1 h-6 bg-white/30"></div>
            <div className="flex items-center gap-2">
              <i className="ri-pages-line text-2xl"></i>
              <span className="font-semibold">
                {actScienceWorksheets.reduce((sum, w) => sum + w.pages, 0)} Total Pages
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative max-w-2xl">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400"></i>
            <input
              type="text"
              placeholder="Search worksheets by title, topic, or skill..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <i className="ri-filter-3-line text-xl text-gray-700"></i>
            <h2 className="text-lg font-bold text-gray-900">Filter by Category</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {filterOptions.map((option) => (
              <button
                key={option}
                onClick={() => setSelectedFilter(option)}
                className={`px-5 py-2.5 rounded-xl font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  selectedFilter === option
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                {option}
                <span className="ml-2 text-sm opacity-75">({getTopicCount(option)})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing <span className="font-bold text-gray-900">{filteredWorksheets.length}</span> worksheet
            {filteredWorksheets.length !== 1 ? 's' : ''}
            {selectedFilter !== 'All' && (
              <span> in <span className="font-bold text-gray-900">{selectedFilter}</span></span>
            )}
          </p>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <a
            href="/test-prep/act/science"
            className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <i className="ri-book-open-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">ACT Science Curriculum</h3>
            <p className="text-sm text-slate-600">Review all skills and practice passages</p>
          </a>

          <a
            href="/act-science-study-plan"
            className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
          >
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-200 transition-colors">
              <i className="ri-calendar-check-line text-2xl text-green-600"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Study Plan</h3>
            <p className="text-sm text-slate-600">Get a weekly prep schedule for test day</p>
          </a>

          <a
            href="/act-science-progress"
            className="block bg-white rounded-xl p-6 border border-slate-200 hover:shadow-lg transition-shadow group"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-200 transition-colors">
              <i className="ri-checkbox-circle-line text-2xl text-purple-600"></i>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-2">Track Progress</h3>
            <p className="text-sm text-slate-600">Mark worksheets complete and see stats</p>
          </a>
        </div>

        {/* Worksheets Grid */}
        {filteredWorksheets.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorksheets.map((worksheet) => (
              <div
                key={worksheet.id}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow group"
              >
                {/* Thumbnail */}
                <div className="relative h-56 bg-gray-100 overflow-hidden">
                  <img
                    src={worksheet.thumbnailUrl}
                    alt={worksheet.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-3 right-3">
                    <span
                      className={`px-3 py-1 rounded-lg text-xs font-bold border ${
                        topicColors[worksheet.topic] || 'bg-gray-100 text-gray-700 border-gray-200'
                      }`}
                    >
                      {worksheet.topic}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                    {worksheet.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {worksheet.description}
                  </p>

                  {/* Meta Info */}
                  <div className="flex items-center gap-3 mb-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <i className="ri-file-text-line"></i>
                      <span>{worksheet.pages} pages</span>
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        worksheet.difficulty === 'Easy'
                          ? 'bg-green-100 text-green-700'
                          : worksheet.difficulty === 'Medium'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {worksheet.difficulty}
                    </span>
                  </div>

                  {/* Download Button */}
                  <a
                    href={worksheet.downloadUrl}
                    download
                    className="block w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold text-center hover:shadow-lg transition-all whitespace-nowrap"
                  >
                    <i className="ri-download-line mr-2"></i>
                    Download PDF
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No worksheets found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedFilter('All');
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}